<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Header -->
    <div class="p-6 border-b border-slate-100 bg-blue-50/50 flex justify-between items-center">
      <h2 class="font-bold text-slate-800 uppercase text-xs">Propriétés du Bloc</h2>
      <button @click="$emit('clear-selection')" class="text-slate-400 hover:text-slate-600">✕</button>
    </div>

    <!-- Properties Content -->
    <div class="flex-1 overflow-y-auto p-6 space-y-6">
      <!-- Position & Taille -->
      <div class="space-y-4">
        <label class="text-[10px] font-black text-slate-400 uppercase">Position & Taille (Pixels Borne)</label>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="text-[9px] text-slate-400 uppercase">Axe X</label>
            <input
              type="number"
              :value="block.x"
              @input="updateProperty('x', Number($event.target.value))"
              class="w-full p-2 bg-slate-50 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label class="text-[9px] text-slate-400 uppercase">Axe Y</label>
            <input
              type="number"
              :value="block.y"
              @input="updateProperty('y', Number($event.target.value))"
              class="w-full p-2 bg-slate-50 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label class="text-[9px] text-slate-400 uppercase">Largeur</label>
            <input
              type="number"
              :value="block.width"
              @input="updateProperty('width', Number($event.target.value))"
              class="w-full p-2 bg-slate-50 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label class="text-[9px] text-slate-400 uppercase">Hauteur</label>
            <input
              type="number"
              :value="block.height"
              @input="updateProperty('height', Number($event.target.value))"
              class="w-full p-2 bg-slate-50 border rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      <!-- Border Radius (for all types except triangle) -->
      <div v-if="block.type !== 'triangle'" class="space-y-2 pt-4 border-t">
        <label class="text-[10px] font-black text-slate-400 uppercase">Coins arrondis</label>
        <input
          type="range"
          :value="block.borderRadius || 0"
          @input="updateProperty('borderRadius', Number($event.target.value))"
          min="0"
          max="200"
          class="w-full"
        />
        <div class="text-xs text-slate-500 text-center">{{ block.borderRadius || 0 }}px</div>
      </div>

      <!-- Text & Style (for text, button, print_ticket, language_button) -->
      <div v-if="hasTextProperties" class="space-y-4 pt-4 border-t">
        <label class="text-[10px] font-black text-slate-400 uppercase">Texte & Style</label>

        <div v-if="block.type !== 'language_button'">
          <label class="text-[10px] text-slate-400">Texte</label>
          <textarea
            :value="block.text"
            @input="updateProperty('text', $event.target.value)"
            rows="2"
            class="w-full p-3 bg-slate-50 rounded-xl border border-slate-100 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          ></textarea>
        </div>

        <!-- Language Button specific properties -->
        <div v-if="block.type === 'language_button'" class="space-y-3">
          <div>
            <label class="text-[10px] text-slate-400">Langue</label>
            <select
              :value="block.languageCode"
              @change="updateProperty('languageCode', $event.target.value)"
              class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
            >
              <option value="">Sélectionner...</option>
              <option v-for="lang in availableLanguages" :key="lang.code" :value="lang.code">
                {{ lang.flag }} {{ lang.label }}
              </option>
            </select>
          </div>
          <div>
            <label class="text-[10px] text-slate-400">Label (optionnel)</label>
            <input
              type="text"
              :value="block.label"
              @input="updateProperty('label', $event.target.value)"
              placeholder="Personnaliser le label"
              class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
            />
          </div>
          <div>
            <label class="text-[10px] text-slate-400">Emoji Flag (optionnel)</label>
            <input
              type="text"
              :value="block.flag"
              @input="updateProperty('flag', $event.target.value)"
              placeholder="Ex: 🇫🇷"
              class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
            />
          </div>
        </div>

        <div>
          <label class="text-[10px] text-slate-400">Police</label>
          <select
            :value="block.fontFamily || 'Plus Jakarta Sans'"
            @change="updateProperty('fontFamily', $event.target.value)"
            class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
          >
            <option value="Plus Jakarta Sans">Plus Jakarta Sans</option>
            <option value="Inter">Inter</option>
            <option value="Poppins">Poppins</option>
            <option value="Roboto">Roboto</option>
            <option value="Montserrat">Montserrat</option>
            <option value="Playfair Display">Playfair Display</option>
            <option value="Space Mono">Space Mono</option>
          </select>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-[10px] text-slate-400">Taille Police</label>
            <input
              type="number"
              :value="block.fontSize || 16"
              @input="updateProperty('fontSize', Number($event.target.value))"
              class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
            />
          </div>
          <div>
            <label class="text-[10px] text-slate-400">Graisse</label>
            <select
              :value="block.fontWeight || '400'"
              @change="updateProperty('fontWeight', $event.target.value)"
              class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
            >
              <option value="400">Normal</option>
              <option value="500">Medium</option>
              <option value="600">Semi-Bold</option>
              <option value="700">Bold</option>
              <option value="800">Extra-Bold</option>
            </select>
          </div>
        </div>

        <div>
          <label class="text-[10px] text-slate-400">Alignement</label>
          <div class="flex gap-2">
            <button
              @click="updateProperty('textAlign', 'left')"
              :class="['flex-1 p-2 rounded-lg border text-xs', block.textAlign === 'left' ? 'bg-blue-500 text-white' : 'bg-slate-50']"
            >
              Gauche
            </button>
            <button
              @click="updateProperty('textAlign', 'center')"
              :class="['flex-1 p-2 rounded-lg border text-xs', block.textAlign === 'center' ? 'bg-blue-500 text-white' : 'bg-slate-50']"
            >
              Centre
            </button>
            <button
              @click="updateProperty('textAlign', 'right')"
              :class="['flex-1 p-2 rounded-lg border text-xs', block.textAlign === 'right' ? 'bg-blue-500 text-white' : 'bg-slate-50']"
            >
              Droite
            </button>
          </div>
        </div>

        <div class="flex items-center gap-4">
          <div class="flex-1">
            <label class="text-[10px] text-slate-400">Couleur texte</label>
            <input
              type="color"
              :value="block.color || '#000000'"
              @input="updateProperty('color', $event.target.value)"
              class="w-full h-8 rounded cursor-pointer"
            />
          </div>
          <div v-if="block.type !== 'text'" class="flex-1">
            <label class="text-[10px] text-slate-400">Fond</label>
            <input
              type="color"
              :value="block.backgroundColor || '#FFFFFF'"
              @input="updateProperty('backgroundColor', $event.target.value)"
              class="w-full h-8 rounded cursor-pointer"
            />
          </div>
        </div>
      </div>

      <!-- Image customization -->
      <div v-if="block.type === 'image'" class="space-y-4 pt-4 border-t">
        <label class="text-[10px] font-black text-slate-400 uppercase">Image</label>

        <div>
          <label class="text-[10px] text-slate-400">URL de l'image</label>
          <slot name="image-picker" :block="block">
            <input
              type="text"
              :value="block.icon || block.src"
              @input="updateProperty('icon', $event.target.value)"
              placeholder="https://..."
              class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
            />
          </slot>
        </div>

        <div>
          <label class="text-[10px] text-slate-400">Ajustement (Object-fit)</label>
          <select
            :value="block.fit || 'contain'"
            @change="updateProperty('fit', $event.target.value)"
            class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
          >
            <option value="cover">Cover (Couvrir)</option>
            <option value="contain">Contain (Contenir)</option>
            <option value="fill">Fill (Remplir)</option>
            <option value="none">None (Aucun)</option>
            <option value="scale-down">Scale-down (Réduire)</option>
          </select>
        </div>

        <div>
          <label class="text-[10px] text-slate-400">Opacité</label>
          <input
            type="range"
            :value="block.opacity ?? 1"
            @input="updateProperty('opacity', Number($event.target.value))"
            min="0"
            max="1"
            step="0.1"
            class="w-full"
          />
          <div class="text-xs text-slate-500 text-center">{{ Math.round((block.opacity ?? 1) * 100) }}%</div>
        </div>
      </div>

      <!-- Shape customization -->
      <div v-if="isShape" class="space-y-4 pt-4 border-t">
        <label class="text-[10px] font-black text-slate-400 uppercase">Forme</label>

        <div>
          <label class="text-[10px] text-slate-400">Couleur de remplissage</label>
          <input
            type="color"
            :value="block.backgroundColor || '#3B82F6'"
            @input="updateProperty('backgroundColor', $event.target.value)"
            class="w-full h-10 rounded cursor-pointer"
          />
        </div>

        <div>
          <label class="text-[10px] text-slate-400">Bordure</label>
          <div class="flex gap-2">
            <input
              type="number"
              :value="block.borderWidth || 0"
              @input="updateProperty('borderWidth', Number($event.target.value))"
              placeholder="Épaisseur"
              class="flex-1 p-2 bg-slate-50 rounded-lg border text-sm"
              min="0"
            />
            <input
              type="color"
              :value="block.borderColor || '#000000'"
              @input="updateProperty('borderColor', $event.target.value)"
              class="w-16 h-10 rounded cursor-pointer"
            />
          </div>
        </div>

        <div>
          <label class="text-[10px] text-slate-400">Opacité</label>
          <input
            type="range"
            :value="block.opacity ?? 1"
            @input="updateProperty('opacity', Number($event.target.value))"
            min="0"
            max="1"
            step="0.1"
            class="w-full"
          />
          <div class="text-xs text-slate-500 text-center">{{ Math.round((block.opacity ?? 1) * 100) }}%</div>
        </div>
      </div>

      <!-- Video customization -->
      <div v-if="block.type === 'video'" class="space-y-4 pt-4 border-t">
        <label class="text-[10px] font-black text-slate-400 uppercase">Vidéo</label>

        <div>
          <label class="text-[10px] text-slate-400">URL iFrame (YouTube, Vimeo, etc.)</label>
          <input
            type="text"
            :value="block.src || block.url"
            @input="updateProperty('src', $event.target.value)"
            placeholder="https://www.youtube.com/embed/..."
            class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
          />
        </div>
      </div>

      <!-- Action for buttons -->
      <div v-if="hasActions" class="space-y-4 pt-4 border-t">
        <label class="text-[10px] font-black text-slate-400 uppercase">Action au clic</label>
        <select
          :value="block.action?.type || 'none'"
          @change="updateActionType($event.target.value)"
          class="w-full p-3 bg-slate-50 rounded-xl border text-sm"
        >
          <option value="none">Aucune</option>
          <option value="queue">File d'attente</option>
          <option value="url">URL</option>
          <option v-if="block.type === 'language_button'" value="language">Sélection langue</option>
        </select>

        <div v-if="block.action?.type === 'queue'">
          <label class="text-[10px] text-slate-400 mb-1 block">Choisir la file</label>
          <slot name="queue-selector" :block="block">
            <select
              :value="block.action.queueId"
              @change="updateActionProperty('queueId', $event.target.value)"
              class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
            >
              <option :value="null">Sélectionner...</option>
            </select>
          </slot>
        </div>

        <input
          v-if="block.action?.type === 'url'"
          type="text"
          :value="block.action.url"
          @input="updateActionProperty('url', $event.target.value)"
          placeholder="https://..."
          class="w-full p-2 bg-slate-50 rounded-lg border text-sm"
        />
      </div>

      <!-- Delete Button -->
      <button
        @click="$emit('delete')"
        class="w-full py-3 text-red-500 bg-red-50 rounded-xl text-xs font-bold mt-4 hover:bg-red-100 transition-colors"
      >
        Supprimer l'élément
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  block: {
    type: Object,
    required: true
  },
  availableLanguages: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update', 'delete', 'clear-selection']);

const hasTextProperties = computed(() => {
  return ['text', 'button', 'print_ticket', 'language_button'].includes(props.block.type);
});

const isShape = computed(() => {
  return ['rectangle', 'circle', 'triangle'].includes(props.block.type);
});

const hasActions = computed(() => {
  return ['button', 'print_ticket', 'language_button'].includes(props.block.type);
});

const updateProperty = (key, value) => {
  emit('update', { key, value });
};

const updateActionType = (type) => {
  emit('update', {
    key: 'action',
    value: { type, queueId: null, url: '' }
  });
};

const updateActionProperty = (key, value) => {
  emit('update', {
    key: 'action',
    value: { ...props.block.action, [key]: value }
  });
};
</script>
